import { ComponentType, lazy, LazyExoticComponent } from 'react';
import type { useLocation, useParams } from 'react-router-dom';
import ProfileSideBarMenu from './components/Profiles/ProfileSideBarMenu';
import UserReportsSideBarMenu from './components/Profiles/UserProfileSidebarMenu';
import Authorization from './Helpers/Authorization';

export interface LegacyRouteProps {
  path?: string;
  location?: ReturnType<typeof useLocation>;
  match?: {
    params: ReturnType<typeof useParams>;
  };
}

interface RouteBase {
  id: number;
  path: string;
  children?: RouteConfig[];
}

export interface RedirectRouteConfig extends RouteBase {
  access: 'redirect';
  redirectTo: string;
  replace?: boolean;
  children?: never;
}

export interface ComponentRouteConfig extends RouteBase {
  access: 'public' | 'protected' | 'none';
  component: LazyExoticComponent<ComponentType<LegacyRouteProps>>;
  meta?: {
    title: string;
    description: string;
  };
  sideBar?: boolean;
  sideBarMenu?: unknown;
  beta?: boolean;
}

export type RouteConfig = RedirectRouteConfig | ComponentRouteConfig;

export const siteTitle = 'Daimler Trucks';
export const siteMetaDescription = 'Daimler Trucks';

export const setTitle = (title?: string): void => {
  document.title = title || siteTitle;
};

export const setMetaDescription = (description?: string): void => {
  document
    .querySelector('meta[name="description"]')
    ?.setAttribute('content', description || siteMetaDescription);
};

const userRole = Authorization.getAuthUserRole();
const profileSideBarMenu =
  userRole === 'ROOTADMIN' ? ProfileSideBarMenu : UserReportsSideBarMenu;
const pageMeta = {
  title: siteTitle,
  description: siteMetaDescription,
};

export const ROUTE: RouteConfig[] = [
  {
    id: 1,
    path: '/',
    access: 'redirect',
    redirectTo: '/dashboard',
    replace: true,
  },
  {
    id: 2,
    path: '/login',
    access: 'public',
    component: lazy(() => import('./components/Login')),
    meta: pageMeta,
  },
  {
    id: 3,
    path: '/forgot-password',
    access: 'public',
    component: lazy(() => import('./components/Forgetpassword')),
    meta: pageMeta,
  },
  {
    id: 4,
    path: '/profiles/driver',
    access: 'protected',
    component: lazy(() => import('./components/Profiles/DriverProfile')),
    sideBar: true,
    sideBarMenu: profileSideBarMenu,
    meta: pageMeta,
  },
  {
    id: 5,
    path: '/profiles/vehicle',
    access: 'protected',
    component: lazy(() => import('./components/Profiles/VehicleProfile')),
    sideBar: true,
    sideBarMenu: profileSideBarMenu,
    meta: pageMeta,
  },
  {
    id: 6,
    path: '/profiles/dealer',
    access: 'protected',
    component: lazy(() => import('./components/Profiles/DealerProfile')),
    sideBar: true,
    sideBarMenu: profileSideBarMenu,
    meta: pageMeta,
  },
  {
    id: 7,
    path: '/profiles',
    access: 'protected',
    component: lazy(() => import('./components/Profiles')),
    meta: pageMeta,
  },
  {
    id: 8,
    path: '/owner-suite',
    access: 'protected',
    component: lazy(() => import('./components/OwnerSuite/index')),
    meta: pageMeta,
  },
  {
    id: 9,
    path: '/cssupport',
    access: 'protected',
    component: lazy(() => import('./components/SupportAdminLogin')),
    meta: pageMeta,
  },
  {
    id: 32,
    path: '/dashboard',
    access: 'protected',
    component: lazy(() => import('./components/Dashboard/index')),
    meta: pageMeta,
  },
  {
    id: 33,
    path: '/trip-management',
    access: 'protected',
    component: lazy(() => import('./components/TripManagement/index')),
    meta: pageMeta,
  },
  {
    id: 31,
    path: '/track-trace',
    access: 'protected',
    component: lazy(() => import('./components/Track-Trace/index')),
    sideBar: true,
    meta: pageMeta,
    beta: true,
  },
  {
    id: 11,
    path: '/profile',
    access: 'protected',
    component: lazy(() => import('./components/Profiles')),
    meta: pageMeta,
  },
  {
    id: 12,
    path: '/settings',
    access: 'protected',
    component: lazy(() => import('./components/Settings')),
    meta: pageMeta,
  },
  {
    id: 131,
    path: '/fleet-healthv2',
    access: 'protected',
    component: lazy(() => import('./components/FleetHealthV2')),
    meta: pageMeta,
    beta: true,
  },
  {
    id: 132,
    path: '/fleet-healthv2/:vehicleId',
    access: 'protected',
    component: lazy(
      () => import('./components/FleetHealthV2/Vehicle/VehicleDtc')
    ),
    meta: pageMeta,
    beta: true,
  },
  {
    id: 133,
    path: '/fleet-efficiency',
    access: 'protected',
    component: lazy(() => import('./components/Fleet-Efficiency')),
    meta: pageMeta,
    beta: true,
  },
  {
    id: 30,
    path: '/reportsv2',
    access: 'protected',
    component: lazy(() => import('./components/ReportsV2')),
    meta: pageMeta,
    beta: true,
  },
  {
    id: 16,
    path: '/profiles/customer-admin',
    access: 'protected',
    component: lazy(() => import('./components/Profiles/CustomerAdmin')),
    sideBar: true,
    sideBarMenu: profileSideBarMenu,
    meta: pageMeta,
  },
  {
    id: 17,
    path: '/profiles/my-profile',
    access: 'protected',
    component: lazy(() => import('./components/Profiles/MyProfile')),
    sideBar: true,
    sideBarMenu: profileSideBarMenu,
    meta: pageMeta,
  },
  {
    id: 27,
    path: '/support',
    access: 'protected',
    component: lazy(() => import('./components/Settings/Help')),
    meta: pageMeta,
  },
  {
    id: 28,
    path: '/alerts',
    access: 'protected',
    component: lazy(() => import('./components/Alerts')),
    sideBar: true,
    meta: pageMeta,
    beta: true,
  },
  {
    id: 29,
    path: '/driving-analysisv2',
    access: 'protected',
    component: lazy(() => import('./components/DrivingAnalysisV2')),
    sideBar: true,
    meta: pageMeta,
    beta: true,
  },
  {
    id: 35,
    path: '/demo',
    access: 'protected',
    component: lazy(() => import('./components/Demo')),
    sideBar: true,
    meta: pageMeta,
    beta: true,
  },
  {
    id: 34,
    path: '/liveTracking',
    access: 'none',
    component: lazy(() => import('./components/Track-Trace/MyLiveTrackBeta')),
  },
];

export default ROUTE;
